import React, { useEffect } from "react";
import { withFormik } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { Modal } from "../../../../components/modal";
import { ValidatedForm } from "../../../../components";
import { type PackRequest } from "../types/state";
import validationSchemas from "../../../../utils/validationSchemas";
import { type PackRequestModalProps, type PackRequestModalWithFormProps } from "./types";
import RequestPackForm from "./RequestPackForm";
import RequestPackModalActions from "./RequestPackModalActions";
import ModalSizes from "../../../../enums/modalSizes";
import {
  accountPackLicenseSelector,
  reset,
} from "features/Accounts/EditAccount/state/slices/editAccountPackLicenseSlice";
import { fetchAccountPackLicense } from "features/Accounts/EditAccount/state/thunks/editAccountPackLicenseThunk";
import { PackRequestTypes } from "enums";
import "./packRequestModal.scss";

export type PackRequestModalAllProps = PackRequestModalWithFormProps & PropsFromRedux;

export const PackRequestModal: React.FC<PackRequestModalAllProps> = (props) => {
  const {
    title,
    values,
    isValid,
    packId,
    accountId,
    requestType,
    resetForm,
    onCancelPackRequest,
    onSubmitPackRequest,
    fetchAccountPackLicense,
    resetAccountPackLicense,
    isOpen,
  } = props;

  useEffect(() => {
    if (
      (isOpen && requestType === PackRequestTypes.RequestPackRenewal) ||
      requestType === PackRequestTypes.RequestTrialConvert
    ) {
      packId && accountId && fetchAccountPackLicense(packId, accountId);
    }
  }, [isOpen, packId, accountId, requestType, fetchAccountPackLicense]);

  const renderModalActions = (closeModal: any) => {
    return (
      <RequestPackModalActions
        onSubmit={() => {
          submit();
          closeModal();
        }}
        onCancel={() => {
          closePackRequestModal();
          closeModal();
        }}
        isValid={isValid}
        submitText={title ?? "Request Pack"}
      />
    );
  };

  const submit = () => {
    onSubmitPackRequest?.(values);
    resetAccountPackLicense();
  };

  const closePackRequestModal = () => {
    resetForm();
    onCancelPackRequest?.();
    resetAccountPackLicense();
  };

  return (
    <Modal
      className={"pack-request-modal"}
      title={title ?? "Request Pack"}
      open={props.isOpen}
      renderModalActions={renderModalActions}
      onCancel={closePackRequestModal}
      size={ModalSizes.tiny}
      dataTestId="request-pack-modal"
    >
      <ValidatedForm formWidthComputer={16} formWidthTablet={16} {...props}>
        <RequestPackForm {...props} />
      </ValidatedForm>
    </Modal>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  accountPackLicense: accountPackLicenseSelector(state).accountPackLicense,
  isLoading: accountPackLicenseSelector(state).isLoading,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    fetchAccountPackLicense: (packId: number, accountId: number) =>
      dispatch(fetchAccountPackLicense(packId, accountId)),
    resetAccountPackLicense: () => dispatch(reset()),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const withPackRequestFormik = withFormik<PackRequestModalProps & PropsFromRedux, PackRequest>({
  validateOnMount: true,
  validationSchema: (props: PackRequestModalProps & PropsFromRedux) =>
    validationSchemas.packRequestValidationSchema(
      props.neededLicenseCount ?? props.accountPackLicense?.assingedLicensesCount,
      !!props.neededLicenseCount,
    ),
  mapPropsToValues: (props: PackRequestModalProps & PropsFromRedux) => {
    return {
      packId: props.packId,
      typeId: props.requestType,
      licensesCount:
        props.neededLicenseCount ??
        ((props.accountPackLicense?.assingedLicensesCount ?? 0) > (props.accountPackLicense?.totalLicensesCount ?? 0)
          ? props.accountPackLicense?.assingedLicensesCount
          : props.accountPackLicense?.totalLicensesCount),
    } as PackRequest;
  },
  handleSubmit: () => {}, // NOSONAR
  enableReinitialize: true,
})(PackRequestModal);

/* istanbul ignore next */
export const ConnectedPackRequestModal = connector(withPackRequestFormik);

export default ConnectedPackRequestModal;
